/**
 * Experimental v2 object schema — barrel export.
 * DO NOT import this anywhere in the existing application yet.
 */

export {
    OPENHASP_PART_SUFFIX,
    MAIN_STYLE,
    INDICATOR_STYLE,
    KNOB_STYLE,
    HA_ACTION_DATA_SUGGESTIONS,
    OBJECT_BACKGROUND_COLOR_REFERENCE,
} from './shared.js'
import {
    BG_GRADIENT_STYLE,
    BACKGROUND_GROUP,
    HA_ACTION_DATA_SUGGESTIONS,
} from './shared.js'

import { normalizeSchema } from './stateFormat.js'

import { arc }      from './objects/arc.js'
import { bar }      from './objects/bar.js'
import { badge }    from './objects/badge.js'
import { connection } from './objects/connection.js'
import { chart }    from './objects/chart.js'
import { btn }      from './objects/btn.js'
import { btnmatrix, mtrx_btn } from './objects/btnmatrix.js'
import { checkbox } from './objects/checkbox.js'
import { cpicker }  from './objects/cpicker.js'
import { dropdown } from './objects/dropdown.js'
import { gauge }    from './objects/gauge.js'
import { h3d }      from './objects/h3d.js'
import { img }      from './objects/img.js'
import { label }    from './objects/label.js'
import { led }      from './objects/led.js'
import { legend }   from './objects/legend.js'
import { line }     from './objects/line.js'
import { linemeter } from './objects/linemeter.js'
import { msgbox, msgbox_btn } from './objects/msgbox.js'
import { obj }      from './objects/obj.js'
import { page }          from './objects/page.js'
import { template_page } from './objects/template_page.js'
import { qrcode }   from './objects/qrcode.js'
import { roller }   from './objects/roller.js'
import { slider }   from './objects/slider.js'
import { spinner }  from './objects/spinner.js'
import { switch_ }  from './objects/switch.js'
import { tabview, tab } from './objects/tabview.js'

/**
 * The registry is the single point where a schema is normalized. Right now that
 * means expanding `states` into the full derived list — a schema declares only
 * its base states and whether it is focusable, and every consumer downstream
 * (getSupportedStates, the Property Editor, history, export, validation) reads
 * the expanded `schema.states` without knowing derivation exists.
 */
function addBackgroundGradientSchema(schema) {
    const gradientParts = Object.entries(schema.parts ?? {}).filter(
        ([, partDef]) => partDef.supportsBackgroundGradient !== false &&
            Object.hasOwn(partDef.properties ?? {}, 'bg_color'),
    )
    if (gradientParts.length === 0) return schema

    const parts = { ...schema.parts }
    const styles = { ...(schema.defaults?.styles ?? {}) }
    for (const [partName, partDef] of gradientParts) {
        const properties = {
            ...partDef.properties,
            bg_color: {
                ...partDef.properties.bg_color,
                label: partDef.properties.bg_color.label ?? 'Background color',
            },
            ...BG_GRADIENT_STYLE,
        }
        const propertyGroups = partName === 'main'
            ? [
                BACKGROUND_GROUP,
                ...(partDef.propertyGroups ?? []).filter(group => group.id !== BACKGROUND_GROUP.id),
            ]
            : partDef.propertyGroups

        parts[partName] = {
            ...partDef,
            properties,
            ...(partName === 'main' ? { propertyGroups } : {}),
        }

        // These are Schema V2 style defaults, not editor-only placeholders.
        // They resolve like every other widget default, remain non-explicit,
        // and therefore don't add new wire fields to untouched designs.
        styles[partName] = {
            ...(styles[partName] ?? {}),
            bg_grad_dir: 0,
            bg_main_stop: 0,
            bg_grad_stop: 255,
        }
    }

    return {
        ...schema,
        parts,
        defaults: { ...(schema.defaults ?? {}), styles },
    }
}

function addAutomationMetadata(schema) {
    const legacyHints = schema.automation?.dataSuggestions ?? HA_ACTION_DATA_SUGGESTIONS
    const actionHints = schema.automation?.actionHints ?? filterActionHints(schema, legacyHints)
    return {
        ...schema,
        automation: {
            ...(schema.automation ?? {}),
            // actionHints is the canonical Designer-side metadata name.
            // dataSuggestions remains as a read-only compatibility alias for
            // older callers and persisted schema snapshots.
            actionHints,
            dataSuggestions: legacyHints,
        },
    }
}

function schemaPayloadNames(schema) {
    const names = new Set()
    for (const definition of Object.values(schema.events ?? {})) {
        for (const key of ['payload', 'rangePayload']) {
            const payload = definition?.[key]
            if (Array.isArray(payload)) payload.forEach(name => names.add(name))
            else if (payload && typeof payload === 'object') Object.keys(payload).forEach(name => names.add(name))
        }
        for (const key of ['payloadMetadata', 'rangePayloadMetadata']) {
            if (definition?.[key]) Object.keys(definition[key]).forEach(name => names.add(name))
        }
    }
    for (const variable of schema.automation?.runtimeVariables ?? []) {
        if (typeof variable === 'string') names.add(variable)
        else if (variable?.name) names.add(variable.name)
    }
    return names
}

function filterActionHints(schema, catalogue) {
    const variables = schemaPayloadNames(schema)
    const properties = new Set([
        ...Object.keys(schema.properties ?? {}),
        ...Object.values(schema.parts ?? {}).flatMap(part => Object.keys(part.properties ?? {})),
    ])
    const valid = suggestion => {
        const value = typeof suggestion === 'string' ? suggestion : suggestion?.value
        if (typeof value !== 'string') return false
        const refs = [...value.matchAll(/{{\s*([a-zA-Z_][\w]*)\b/g)].map(match => match[1])
        if (refs.some(variable => !variables.has(variable))) return false
        const objectRefs = [...value.matchAll(/(?:^|[^\w])object\.([a-zA-Z_][\w]*)/g)].map(match => match[1])
        return objectRefs.every(property => properties.has(property))
    }
    return Object.fromEntries(Object.entries(catalogue ?? {}).map(([domain, services]) => [
        domain,
        Object.fromEntries(Object.entries(services ?? {}).map(([service, suggestions]) => [
            service,
            Array.isArray(suggestions) ? suggestions.filter(valid) : [],
        ])),
    ]))
}

function findProperty(schema, name) {
    if (schema.properties?.[name]) return { scope: 'properties', name }
    if (name === 'w' && schema.properties?.width) return { scope: 'properties', name: 'width', wireName: 'w' }
    if (name === 'h' && schema.properties?.height) return { scope: 'properties', name: 'height', wireName: 'h' }
    for (const [partName, part] of Object.entries(schema.parts ?? {})) {
        if (part.properties?.[name]) return { scope: 'parts', partName, name }
    }
    return null
}

/**
 * Migrate the remaining legacy binding-property lists into explicit runtime
 * metadata at the schema registry boundary. A schema that already declares
 * runtime.writable is authoritative and is never widened by this fallback.
 */
function addRuntimeMetadata(schema) {
    const hasRuntimeMetadata = Object.entries(schema.properties ?? {}).some(([, definition]) => definition?.runtime?.writable === true) ||
        Object.values(schema.parts ?? {}).some(part => Object.values(part.properties ?? {}).some(definition => definition?.runtime?.writable === true))
    if (hasRuntimeMetadata) return schema

    const bindings = schema.bindings?.properties ?? {}
    if (Object.keys(bindings).length === 0) return schema

    const properties = { ...(schema.properties ?? {}) }
    const parts = Object.fromEntries(Object.entries(schema.parts ?? {}).map(([partName, part]) => [
        partName,
        { ...part, properties: { ...(part.properties ?? {}) } },
    ]))

    const writable = (definition, wireName = '') => {
        const copy = { ...definition }
        Object.defineProperty(copy, 'runtime', {
            value: {
                ...(definition.runtime ?? {}),
                writable: true,
                ...(wireName ? { wireName } : {}),
            },
            enumerable: false,
            configurable: true,
        })
        return copy
    }

    for (const [bindingName, bindingDefinition] of Object.entries(bindings)) {
        const direct = findProperty(schema, bindingName)
        if (direct?.scope === 'properties') {
            const definition = properties[direct.name]
            properties[direct.name] = writable(definition, direct.wireName ?? '')
            continue
        }

        if (direct?.scope === 'parts') {
            const definition = parts[direct.partName].properties[direct.name]
            parts[direct.partName].properties[direct.name] = writable(definition)
            continue
        }

        // A nested binding entry describes a specific style part, e.g.
        // bindings.properties.indicator.bg_color.
        if (schema.parts?.[bindingName] && bindingDefinition && typeof bindingDefinition === 'object') {
            const part = parts[bindingName]
            for (const propertyName of Object.keys(bindingDefinition)) {
                if (!part.properties?.[propertyName]) continue
                const definition = part.properties[propertyName]
                part.properties[propertyName] = writable(definition)
            }
        }
    }

    return { ...schema, properties, parts }
}

const registerSchemas = schemas => Object.fromEntries(
    Object.entries(schemas).map(([objType, schema]) => [
        objType,
        normalizeSchema(addRuntimeMetadata(addAutomationMetadata(addBackgroundGradientSchema(schema)))),
    ]),
)

export const objectSchemaV2 = registerSchemas({
    '3d': h3d,
    arc,
    bar,
    badge,
    connection,
    chart,
    btn,
    btnmatrix,
    checkbox,
    cpicker,
    dropdown,
    gauge,
    h3d,
    img,
    label,
    led,
    legend,
    line,
    linemeter,
    msgbox,
    msgbox_btn,
    mtrx_btn,
    obj,
    page,
    template_page,
    qrcode,
    roller,
    slider,
    spinner,
    switch: switch_,
    tab,
    tabview,
})
